// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';

import { useI18n } from '@/i18n';
import type { UserSession } from '@/app/user-context';
import { useDebugTraceState } from '@/stores/debugTraceState';
import appColors from '@/theme/colors';

type DebugModalProps = {
  open: boolean;
  onClose: () => void;
  user: UserSession | null;
  playerId: string | null;
  roundId: string | null;
  tourId: string | null;
  roundName: string | null;
  tourName: string | null;
  loading: boolean;
  coachMarks: Array<{ key: string; shownCount: number; dismissed: boolean }>;
  coachMarksLoading: boolean;
  onDisable: () => void;
  onResetCoachMarks: () => Promise<void>;
};

export const DebugModal = ({
  open,
  onClose,
  user,
  playerId,
  roundId,
  tourId,
  roundName,
  tourName,
  loading,
  coachMarks,
  coachMarksLoading,
  onDisable,
  onResetCoachMarks,
}: DebugModalProps) => {
  const { t } = useI18n();
  const { holeViewTraceId, holeScoreTraceId } = useDebugTraceState();
  const canCopy = typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText;
  const [isResettingCoachMarks, setIsResettingCoachMarks] = useState(false);
  const [resetCoachMarkStatus, setResetCoachMarkStatus] = useState<string | null>(null);

  const handleCopy = async () => {
    if (!canCopy) return;
    const lines = [
      `userId=${user?.id ?? ''}`,
      `playerId=${playerId ?? ''}`,
      `roundId=${roundId ?? ''}`,
      `tourId=${tourId ?? ''}`,
      `holeViewTraceId=${holeViewTraceId ?? ''}`,
      `holeScoreTraceId=${holeScoreTraceId ?? ''}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
    } catch {
      // Ignore clipboard failures.
    }
  };
  const handleCopyValue = async (value: string | null | undefined) => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(value ?? '');
    } catch {
      // Ignore clipboard failures.
    }
  };
  const handleResetCoachMarks = async () => {
    if (isResettingCoachMarks) return;

    onClose();
    setIsResettingCoachMarks(true);
    setResetCoachMarkStatus(null);
    try {
      await onResetCoachMarks();
      setResetCoachMarkStatus(null);
    } catch {
      // Modal is closed before reset; keep failure silent here.
    } finally {
      setIsResettingCoachMarks(false);
    }
  };
  const renderRow = (label: string, value: string | null | undefined, copyKey?: string) => (
    <View style={styles.row} key={label}>
      <View style={styles.rowHeader}>
        <Text style={styles.label}>{label}</Text>
        {canCopy && copyKey ? (
          <Pressable
            style={styles.copyButtonInline}
            onPress={() => handleCopyValue(value)}
            accessibilityLabel={t('debug.copyIdLabel', { label })}
          >
            <FontAwesome name="copy" size={12} color={appColors.text.primary} />
          </Pressable>
        ) : null}
      </View>
      <Text style={styles.value}>{value ?? t('debug.unknown')}</Text>
    </View>
  );

  return (
    <Modal transparent visible={open} animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <View style={styles.card}>
          <View style={styles.headerRow}>
            <Text style={styles.title}>{t('debug.title')}</Text>
            {canCopy ? (
              <Pressable style={styles.copyButton} onPress={handleCopy} accessibilityLabel={t('debug.copyIdsLabel')}>
                <FontAwesome name="copy" size={16} color={appColors.text.primary} />
              </Pressable>
            ) : null}
          </View>
          {renderRow(t('debug.userLabel'), user?.forename ?? t('debug.unknown'))}
          {renderRow(t('debug.userIdLabel'), user?.id ?? t('debug.unknown'), 'userId')}
          {renderRow(t('debug.playerIdLabel'), playerId ?? t('debug.unknown'), 'playerId')}
          {renderRow(t('debug.roundLabel'), roundId ?? t('debug.unknown'), 'roundId')}
          {renderRow(t('debug.roundNameLabel'), roundName ?? t('debug.unknown'))}
          {renderRow(t('debug.tourLabel'), tourId ?? t('debug.unknown'), 'tourId')}
          {renderRow(t('debug.tourNameLabel'), tourName ?? t('debug.unknown'))}
          {renderRow(t('debug.holeViewTraceLabel'), holeViewTraceId ?? t('debug.traceNone'), 'holeViewTraceId')}
          {renderRow(t('debug.holeScoreTraceLabel'), holeScoreTraceId ?? t('debug.traceNone'), 'holeScoreTraceId')}
          <Text style={styles.sectionTitle}>{t('debug.coachMarksTitle')}</Text>
          {coachMarksLoading ? <Text style={styles.meta}>{t('debug.loading')}</Text> : null}
          {!coachMarksLoading && coachMarks.length === 0 ? (
            <Text style={styles.meta}>{t('debug.coachMarksEmpty')}</Text>
          ) : null}
          {coachMarks.map((item) =>
            renderRow(
              t('debug.coachMarkLabel', { key: item.key }),
              item.dismissed ? `${item.shownCount} (${t('debug.coachMarkDismissed')})` : `${item.shownCount}`
            )
          )}
          {loading ? <Text style={styles.meta}>{t('debug.loading')}</Text> : null}
          <Pressable style={styles.close} onPress={onClose}>
            <Text style={styles.closeText}>{t('debug.close')}</Text>
          </Pressable>
          <Pressable style={styles.disable} onPress={onDisable}>
            <Text style={styles.disableText}>{t('debug.disable')}</Text>
          </Pressable>
          <Pressable
            style={[styles.resetCoachMarks, isResettingCoachMarks && styles.resetCoachMarksDisabled]}
            onPress={() => void handleResetCoachMarks()}
            disabled={isResettingCoachMarks}
          >
            <Text style={styles.resetCoachMarksText}>
              {isResettingCoachMarks ? t('debug.resetCoachMarksRunning') : t('debug.resetCoachMarks')}
            </Text>
          </Pressable>
          {resetCoachMarkStatus ? <Text style={styles.resetCoachMarksStatus}>{resetCoachMarkStatus}</Text> : null}
        </View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.14)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    minWidth: 280,
    width: '86%',
    maxWidth: 360,
    padding: 18,
    borderRadius: 12,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#A1D493',
    shadowColor: '#C8C8B0',
    shadowOpacity: 0.35,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    color: '#f4f6fb',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
  },
  copyButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginTop: -2,
  },
  copyButtonInline: {
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  row: {
    marginBottom: 8,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    color: '#8ecf9a',
    fontSize: 12,
    fontWeight: '600',
  },
  value: {
    color: appColors.text.primary,
    fontSize: 13,
    marginTop: 2,
  },
  meta: {
    color: '#8FA08B',
    fontSize: 12,
    marginTop: 4,
  },
  sectionTitle: {
    color: '#2d6b66',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 8,
    marginBottom: 4,
  },
  close: {
    alignSelf: 'flex-end',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: '#C8C8B0',
    marginTop: 6,
  },
  closeText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  disable: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d27b',
    marginTop: 10,
  },
  disableText: {
    color: '#d1d27b',
    fontWeight: '600',
  },
  resetCoachMarks: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#85f0c1',
    marginTop: 10,
  },
  resetCoachMarksDisabled: {
    opacity: 0.65,
  },
  resetCoachMarksText: {
    color: '#85f0c1',
    fontWeight: '600',
  },
  resetCoachMarksStatus: {
    color: '#2d6b66',
    fontSize: 12,
    marginTop: 6,
  },
});
